import { Box, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Survey } from "survey-react-ui";
import { Model, settings } from "survey-core";
import "survey-core/defaultV2.css";
import { toast } from "react-toastify";

import { __API_URL__ } from "../../SERVER_URL";
import { getSafeLocale } from "../../utils/surveyLocalization";

const getDraftKey = (sessionId, advancedSurveyId) =>
  `survey_draft_${sessionId}_${advancedSurveyId}`;

const AdvancedSurveyWebView = () => {
  const { sessionId, advancedSurveyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const completed = searchParams.get("completed") === "true";
  const token = searchParams.get("token");
  const lang = getSafeLocale(searchParams.get("lang"));

  const [surveyModel, setSurveyModel] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!advancedSurveyId || !token) return;

    settings.readOnly.enableValidation = true;

    const fetchSurvey = async () => {
      try {
        const res = await axios.get(
          `${__API_URL__}/api/survey/advanced-surveys/${advancedSurveyId}/`,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );

        const surveyJson = res?.data?.survey_json;
        if (!surveyJson) {
          toast.error("Survey definition not found");
          return;
        }

        const model = new Model(surveyJson);
        model.locale = lang;
        model.showCompletedPage = false;

        setSurveyModel(model);
      } catch (error) {
        toast.error("Failed to load survey");
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [advancedSurveyId, token]);

  useEffect(() => {
    if (!surveyModel || !sessionId || !advancedSurveyId) return;

    const fetchResponse = async () => {
      try {
        const res = await axios.get(`${__API_URL__}/api/survey/advanced-survey-response/`, {
          params: {
            photo_session_id: sessionId,
            advanced_survey_id: advancedSurveyId,
          },
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        if (res?.data?.response_json) {
          surveyModel.data = res.data.response_json;
        }
      } catch (error) {
        // 404 is acceptable → means no response yet
      }

      // localStorage draft takes priority over backend response (it's more recent)
      try {
        const raw = localStorage.getItem(getDraftKey(sessionId, advancedSurveyId));
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft.data) surveyModel.data = draft.data;
          if (draft.currentPageNo != null) surveyModel.currentPageNo = draft.currentPageNo;
        }
      } catch (_) {
        // corrupt localStorage entry — ignore
      }
    };

    fetchResponse();
  }, [surveyModel, sessionId, advancedSurveyId, token]);

  useEffect(() => {
    if (!surveyModel || !sessionId || !advancedSurveyId) return;

    const saveDraft = (sender) => {
      try {
        localStorage.setItem(
          getDraftKey(sessionId, advancedSurveyId),
          JSON.stringify({ data: sender.data, currentPageNo: sender.currentPageNo })
        );
      } catch (_) {
        // storage quota exceeded or private browsing — ignore
      }
    };

    surveyModel.onValueChanged.add(saveDraft);
    surveyModel.onCurrentPageChanged.add(saveDraft);

    return () => {
      surveyModel.onValueChanged.remove(saveDraft);
      surveyModel.onCurrentPageChanged.remove(saveDraft);
    };
  }, [surveyModel, sessionId, advancedSurveyId]);

  useEffect(() => {
    if (!surveyModel) return;

    surveyModel.onComplete.add(async (sender) => {
      if (isSubmittingRef.current) return;

      const hasErrors = !sender.validate();

      if (hasErrors) {
        toast.error("Please fix validation errors before completing the survey");
        return;
      }

      try {
        isSubmittingRef.current = true;

        await axios.post(
          `${__API_URL__}/api/survey/advanced-survey-responses/`,
          {
            photo_session: sessionId,
            advanced_survey: advancedSurveyId,
            response_json: sender.data,
          },
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );

        toast.success("Survey completed successfully");

        localStorage.removeItem(getDraftKey(sessionId, advancedSurveyId));

        // Add completed=true to URL
        searchParams.set("completed", "true");
        setSearchParams(searchParams);
      } catch (error) {
        toast.error(error?.response?.data?.error || "Failed to save survey response");
      } finally {
        isSubmittingRef.current = false;
      }
    });

    return () => {
      surveyModel.onComplete.clear();
    };
  }, [surveyModel, sessionId, advancedSurveyId, token, searchParams, setSearchParams]);

  if (completed) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 2,
        }}
      >
        <Box sx={{ fontSize: "2.25rem", fontWeight: 600, color: "success.main" }}>
          Survey has been filled successfully.
        </Box>
      </Box>
    );
  }

  if (loading || !surveyModel) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflow: "auto",
        p: 2,
      }}
    >
      <Survey model={surveyModel} />
    </Box>
  );
};

export default AdvancedSurveyWebView;
